import React, { useState } from 'react';
import { GroupedInput } from '../Regimens/GroupedInput';
import { Autocomplete } from '../Autocomplete';
import { useRegimenOptions } from './useRegimenOptions';
import { DOSAGE_UNITS, type IntakeRecord } from '../../types/data';
import type { DosageUnit } from '../../types/data';

interface IntakeFormProps {
  onSubmit: (record: IntakeRecord) => void;
  initialData?: IntakeRecord;
  onCancel?: () => void;
}

const defaultRecord: IntakeRecord = {
  name: '',
  dosage_taken: undefined,
  dosage_units: 'mg',
  intake_time: new Date().toISOString()
};

export const IntakeForm: React.FC<IntakeFormProps> = ({
  onSubmit,
  initialData,
  onCancel
}) => {
  const [formData, setFormData] = useState<IntakeRecord>(initialData || defaultRecord);
  const [errors, setErrors] = useState<Partial<Record<keyof IntakeRecord, string>>>({});
  const regimenOptions = useRegimenOptions();

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof IntakeRecord, string>> = {};

    if (!formData.name) {
      newErrors.name = 'Regimen name is required';
    }
    if (!formData.dosage_taken || formData.dosage_taken <= 0) {
      newErrors.dosage_taken = 'Please enter a valid dosage';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange = <K extends keyof IntakeRecord>(
    key: K,
    value: IntakeRecord[K]
  ) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg shadow-md">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <Autocomplete
            label="Regimen"
            value={formData.name}
            onChange={(value) => handleChange('name', value)}
            options={regimenOptions}
            error={errors.name}
            required
            tooltip="Select an existing regimen or type to create a new one"
            allowNew
            placeholder="Type to search or create new..."
          />
        </div>

        <GroupedInput
          label="Dosage Taken"
          value={formData.dosage_taken || ''}
          units={formData.dosage_units || 'mg'}
          options={DOSAGE_UNITS}
          onValueChange={(value) => handleChange('dosage_taken', Number(value))}
          onUnitsChange={(value) => handleChange('dosage_units', value as DosageUnit)}
          type="number"
          min={0}
          error={errors.dosage_taken}
          required
          tooltip="Enter the amount taken"
        />
      </div>

      <div className="flex justify-end gap-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          {initialData ? 'Update' : 'Record'} Intake
        </button>
      </div>
    </form>
  );
};